/**
 * Module: WaitScreen
 * Role: Delivery wait screen (stage 2). Shows timeline and countdown.
 * Uses: tokens (P, S, T, G), flow-types (Checkpoint, SlotFlowRun)
 * Used by: FlowScreen (case 'WAIT')
 *
 * Design ref: ai/designs/miniapp/project/pantene-flow.jsx PF_Delivery (L728-815)
 *
 * L-250: WAIT state determined by kind==='WAIT' AND flow_run.status, NOT by date.
 * FTR-1224: buyer-initiated self-advance via in-app CTA is the supported exit
 *   (no auto-advance cron, no WB/Ozon "delivered" webhook exists). Anti-fraud
 *   gate lives on the NEXT step (pickup_status = 3 photos from PVZ).
 */

import type { CSSProperties } from 'react';
import type { Checkpoint, SlotFlowRun, CheckpointAttempt } from '../../../api/flow-types';
import { StepStrip } from '../StepStrip';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { P, S, T } from '../../../styles/tokens';

interface WaitScreenProps {
  checkpoint: Checkpoint;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempt?: CheckpointAttempt;
  onCta?: () => void;
}

/** Parse auto_advance_after_ms from checkpoint metadata into remaining days estimate. */
function computeRemainingDays(checkpoint: Checkpoint): number | null {
  const ms = checkpoint.metadata?.auto_advance_after_ms;
  if (typeof ms !== 'number' || ms <= 0) return null;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Slug-aware default CTA label for WAIT checkpoints (FTR-1224).
 * Корректность на всех 18 WAIT-чекпоинтах:
 *   - wait_2_weeks / wait_4_weeks (usage-period, товар давно получен) → «✅ Продолжить»
 *   - payment_info (kind=WAIT, оплата)                                → «Продолжить»
 *   - wait_delivery / wait_for_delivery (доставка, default)           → «📦 Товар получен»
 * Primary source — checkpoint.metadata.cta_label (если задан в YAML); этот helper = fallback.
 */
function defaultWaitCtaLabel(slug: string): string {
  if (slug.includes('wait_2_weeks') || slug.includes('wait_4_weeks')) return '✅ Продолжить';
  if (slug === 'payment_info') return 'Продолжить';
  return '📦 Товар получен';
}

const timelineStages = [
  { key: 'ordered',  label: 'Заказано' },
  { key: 'packed',   label: 'Собрано' },
  { key: 'transit',  label: 'В пути' },
  { key: 'pvz',      label: 'В ПВЗ' },
  { key: 'received', label: 'Получено' },
];

// Active stage for WAIT = 'transit' (reasonable default for "товар едет")
const ACTIVE_INDEX = 2;

export function WaitScreen({ checkpoint, flowRun: _flowRun, checkpoints, onCta }: WaitScreenProps) {
  // L-250: flowRun.status available for future status-driven rendering.
  void _flowRun;
  const remainingDays = computeRemainingDays(checkpoint);

  // Step progress = linear over TOP-LEVEL checkpoints (parentCount), consistent
  // with all other flow screens. position is sibling-scoped, so position+1 is the
  // 1-based index among top-level steps. Replaced the Pantene-only 6-stage strip
  // (PFStepStrip) so the «X из N» denominator matches every other flow screen.
  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const wrapStyle: CSSProperties = {
    padding: '0 16px 24px',
    minHeight: '100dvh',
    background: P.bg,
    display: 'flex',
    flexDirection: 'column',
  };

  const cardStyle: CSSProperties = {
    background: P.card,
    borderRadius: 16,
    padding: 16,
    border: `1px solid ${P.line}`,
    marginTop: 16,
  };

  return (
    <div style={wrapStyle} data-testid="wait-screen">
      {/* Step strip */}
      <div style={{ padding: '16px 0 12px' }}>
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      {/* Title */}
      <div style={{ font: T.title2, color: S.label, marginTop: 8, lineHeight: 1.2 }}>
        📦 Товар едет
      </div>
      <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 4 }}>
        Нажмите кнопку ниже, когда заберёте товар — откроется следующий шаг
      </div>

      {/* Timeline card */}
      <div style={cardStyle}>
        <div style={{ font: T.caption, color: S.secondaryLabel, fontWeight: 600, textTransform: 'uppercase' }}>
          СТАТУС ДОСТАВКИ
        </div>

        <div style={{ marginTop: 14, position: 'relative', paddingLeft: 22 }}>
          {/* Vertical track line */}
          <div style={{
            position: 'absolute', left: 5, top: 6, bottom: 6,
            width: 2, background: '#E8E3D4', borderRadius: 1,
          }} />

          {timelineStages.map((s, i) => {
            const done = i < ACTIVE_INDEX;
            const active = i === ACTIVE_INDEX;
            return (
              <div key={s.key} style={{ position: 'relative', paddingBottom: i === timelineStages.length - 1 ? 0 : 12 }}>
                {/* Dot */}
                <div style={{
                  position: 'absolute', left: -20, top: 3,
                  width: 12, height: 12, borderRadius: 6,
                  background: done ? P.success : active ? P.terracotta : P.card,
                  border: `2px solid ${done ? P.success : active ? P.terracotta : '#E8E3D4'}`,
                  boxShadow: active ? '0 0 0 5px rgba(226,114,91,0.20)' : 'none',
                }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{
                    font: T.footnote,
                    color: done || active ? S.label : S.tertiaryLabel,
                    fontWeight: active ? 600 : 400,
                  }}>{s.label}</div>
                  <div style={{ font: T.caption, color: S.secondaryLabel }}>
                    {done ? '✓' : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Remaining time badge */}
      {remainingDays !== null && (
        <div style={{
          marginTop: 12,
          padding: '10px 14px',
          borderRadius: 14,
          background: 'rgba(53,107,107,0.08)',
          border: '0.5px solid rgba(53,107,107,0.15)',
          font: T.footnote,
          color: P.jade,
          fontWeight: 600,
          textAlign: 'center',
        }}>
          ⏳ Осталось ~{remainingDays} {daysWord(remainingDays)}
        </div>
      )}

      {/* Tips card */}
      <div style={{ ...cardStyle, marginTop: 14 }}>
        <div style={{ font: T.caption, fontWeight: 700, color: S.label, marginBottom: 8 }}>
          💡 Пока ждёшь — подготовься:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <TipRow>Подготовь телефон и хорошее освещение для следующего шага</TipRow>
          <TipRow>Посмотри ещё раз бриф — там указано, что нужно сделать</TipRow>
          <TipRow>Убедись, что мобильный интернет работает</TipRow>
        </div>
      </div>

      {onCta && (
        <WizardCtaBar
          label={
            (typeof checkpoint.metadata?.cta_label === 'string'
              ? checkpoint.metadata.cta_label
              : undefined) ?? defaultWaitCtaLabel(checkpoint.slug)
          }
          onClick={onCta}
        />
      )}
    </div>
  );
}

function TipRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', font: T.footnote, color: S.label }}>
      <span style={{ color: P.success, flexShrink: 0 }}>✓</span>
      <span>{children}</span>
    </div>
  );
}

/** Russian day declension: день/дня/дней */
function daysWord(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 19) return 'дней';
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}
